// open-campaign-page.ts — go to a campaign's page (/campaigns/:id).
//
// The campaign page is where a campaign is viewed AND edited (Messages,
// Audience, When), so every "open this campaign" and every "Edit campaign" in
// the product lands here: the planner calendar, its sidebar list, and the
// campaign row menu.
//
// Two routers: on the website the app's browser router; in the extension the
// campaigns overlay's memory router, plus opening that overlay. (`useRouter` in
// the extension opens a new browser TAB, which is not where this belongs.)

import { useCallback } from 'react';

import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { useRouter } from '@common-reducers/hooks/use-router';
import { openBulkDashboard } from '@common-reducers/CampaignReducer';
import { mainSliceActions } from '@common-reducers/MainReducer';
import { isExtensionContext } from '@common/services/detect-context';
import { launchCampaignSurface } from '@common/components/top-menu/campaign-launch';
import { campaignsMemoryRouter } from './campaigns-routes';
import { flowPath } from '@common/utils/clear-flow';

/**
 * The page's path. `id` is the DRIP id for any step of a sequence (the page
 * renders the whole sequence) and the campaign's own id for a standalone one.
 * `step` marks the step the user came from (the calendar chip that was clicked).
 * It rides in the QUERY because both routers parse a search string out of a
 * navigate() path, whereas `window.location` means nothing in the overlay.
 */
export const campaignPagePath = (id: string, step?: number, from?: 'calendar'): string => {
    const query = new URLSearchParams();
    if (typeof step === 'number') query.set('step', String(step));
    // Where the page was opened from — its Back returns there (campaigns-redesign-view).
    if (from) query.set('from', from);
    const qs = query.toString();
    return flowPath(`/campaigns/${id}${qs ? `?${qs}` : ''}`);
};

/**
 * Navigate to a campaign page from anywhere, in either context.
 *
 * In the extension there are no URLs: the page is the campaigns overlay pointed
 * at `path` by its memory router. Through `launchCampaignSurface` (FE#318), so the
 * overlay is OPENED before the router is touched and a router throw can never
 * leave it shut — which is exactly what "clicking a campaign does nothing" was.
 * The planner overlay is closed on the way, as View logs does: it sits over the
 * campaigns overlay, so leaving it open hid the page that had just opened.
 */
export function navigateToCampaignPage(
    path: string,
    deps: { dispatch: ReturnType<typeof useAppDispatch>; navigateWeb: (path: string) => void },
): void {
    if (isExtensionContext()) {
        launchCampaignSurface({
            setSurface: () => deps.dispatch(mainSliceActions.closeCalendarView()),
            openPanel: () => deps.dispatch(openBulkDashboard()),
            navigate: () => { void campaignsMemoryRouter.navigate(path); },
            onNavigateError: (error) => console.error('[open-campaign-page] navigate failed', path, error),
        });
    } else {
        deps.navigateWeb(path);
    }
}

/**
 * Go to ANY campaigns route (`/campaigns`, `/campaigns/:id/logs`, …) from outside
 * the campaigns surface — the hook form, for components rendered under the
 * website's router (or in the extension, where no router is needed). This is the
 * one correct way: the extension overlay is ROUTE-driven, so the legacy
 * `setActiveBulkView(...)` + `openBulkDashboard()` pattern opens it on whatever
 * stale route it was last left at.
 */
export function useNavigateCampaigns(): (path: string) => void {
    const dispatch = useAppDispatch();
    const router = useRouter();
    return useCallback((path: string) => {
        navigateToCampaignPage(path, { dispatch, navigateWeb: (p) => router.navigate(p) });
    }, [dispatch, router]);
}

/** A campaign's page (see `campaignPagePath`). */
export function useOpenCampaignPage(): (id: string, step?: number, from?: 'calendar') => void {
    const navigateCampaigns = useNavigateCampaigns();
    return useCallback((id: string, step?: number, from?: 'calendar') => {
        navigateCampaigns(campaignPagePath(id, step, from));
    }, [navigateCampaigns]);
}
